import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { Icons } from '../../../shared/helpers/icons';
import { IUserInfo } from '../../../shared/models/contact';
import { ICustomEopSection } from '../../../shared/models/eopSection';
import { UserDataService } from '../../../shared/services/userData.service';
import { CustomEopService } from '../../../shared/services/customEop.service';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { PopupMessageComponent } from '../../components/popup-message/popup-message.component';
import { IntroductionComponent } from '../../components/introduction/introduction.component';
import { PrvNextButtonsComponent } from '../../components/prv-next-buttons/prv-next-buttons.component';
import { DeleteConfirmationComponent } from '../../components/delete-confirmation/delete-confirmation.component';
import { AddCustomPageComponent } from '../add-custom-page/add-custom-page.component';
import { EditCustomPageComponent } from '../edit-custom-page/edit-custom-page.component';
import { buildPlanPageUrl, getPlanSlugFromUrl } from '../../eop-config/eop-page-config';

@Component({
  selector: 'custom-pages-list',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    SpinnerComponent,
    PopupMessageComponent,
    IntroductionComponent,
    PrvNextButtonsComponent,
    DeleteConfirmationComponent,
    AddCustomPageComponent,
    EditCustomPageComponent
  ],
  templateUrl: './custom-pages-list.component.html',
  styleUrl: './custom-pages-list.component.css'
})
export class CustomPagesListComponent implements OnInit {

  icons = Icons;
  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;

  customPages: ICustomEopSection[] = [];

  showAddCustomPage: boolean = false;
  showEditCustomPage: boolean = false;
  editItem: ICustomEopSection | null = null;
  showDeleteConfirmation: boolean = false;
  deleteItem: ICustomEopSection | null = null;

  loading: boolean = false;
  messageModal: string | null = null;
  popupMessage: string | null = null;

  constructor(
    private router: Router,
    private userDataService: UserDataService,
    private customEopService: CustomEopService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    this.loadPages();
  }

  loadPages(): void {
    if (!this.currentBuildingId) return;
    this.loading = true;
    this.customEopService.getCustomEopByBuildingId(this.currentBuildingId).subscribe({
      next: (response) => {
        this.loading = false;
        const pages = response && response.data ? response.data : [];
        this.customPages = [...pages].sort((a, b) => (a.title || '').localeCompare(b.title || ''));
      },
      error: (error: any) => {
        this.loading = false;
        this.messageModal = 'unsuccess';
        this.popupMessage = error.message;
      }
    });
  }

  openPage(page: ICustomEopSection): void {
    if (!this.currentBuildingId || !page?.id) return;
    const plan = getPlanSlugFromUrl(this.router.url);
    this.router.navigateByUrl(buildPlanPageUrl(plan, this.currentBuildingId, 'custom-page/' + page.id));
  }

  openAdd(): void { this.showAddCustomPage = true; }
  closeAdd(): void { this.showAddCustomPage = false; }

  openEdit(page: ICustomEopSection): void { this.editItem = page; this.showEditCustomPage = true; }
  closeEdit(): void { this.showEditCustomPage = false; this.editItem = null; }

  handleSaved(message: { type: string, message: string }): void {
    this.closeAdd();
    this.closeEdit();
    this.loadPages();
    this.messageModal = message.type;
    this.popupMessage = message.message;
  }

  openDelete(page: ICustomEopSection): void { this.deleteItem = page; this.showDeleteConfirmation = true; }
  closeDelete(): void { this.showDeleteConfirmation = false; this.deleteItem = null; }
  handleDelete(page: ICustomEopSection): void {
    this.showDeleteConfirmation = false;
    if (!page?.id) { this.closeDelete(); return; }
    this.loading = true;
    this.customEopService.deleteCustomPage(String(page.id)).subscribe({
      next: () => {
        this.loading = false;
        this.messageModal = 'success';
        this.popupMessage = 'This occurred after deleting a custom page.';
        this.deleteItem = null;
        this.loadPages();
      },
      error: (error: any) => {
        this.loading = false;
        this.messageModal = 'unsuccess';
        this.popupMessage = error.message;
        this.deleteItem = null;
      }
    });
  }

  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }
}
